import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/attendance-control`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function apiError(data, fallback) {
  if (!data || typeof data !== "object") return fallback;
  const d = typeof data.details === "string" ? data.details.trim() : "";
  const m = typeof data.message === "string" ? data.message.trim() : "";
  if (d && m) return `${m}: ${d}`;
  return d || m || fallback;
}

/* ================= PLACES ================= */

export async function searchAttendancePlaces(query, { lat, lng, cityHint } = {}) {
  const q = String(query ?? "").trim();
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (lat != null && Number.isFinite(Number(lat))) params.set("lat", String(lat));
  if (lng != null && Number.isFinite(Number(lng))) params.set("lng", String(lng));
  if (cityHint) params.set("cityHint", String(cityHint));
  const res = await authFetch(`${BASE}/places/search?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Place search failed"));
  return Array.isArray(data.data) ? data.data : [];
}

export async function reverseAttendancePlace(latitude, longitude) {
  const params = new URLSearchParams({
    lat: String(latitude),
    lng: String(longitude),
  });
  const res = await authFetch(`${BASE}/places/reverse?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Could not resolve address"));
  return data.data ?? null;
}

/* ================= LOCATIONS ================= */

export async function fetchAttendanceLocations() {
  const res = await authFetch(`${BASE}/locations`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load locations"));
  return Array.isArray(data.data) ? data.data : [];
}

export async function createAttendanceLocation(payload) {
  const res = await authFetch(`${BASE}/locations`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to save location"));
  return data.data;
}

export async function updateAttendanceLocation(id, payload) {
  const res = await authFetch(`${BASE}/locations/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to update location"));
  return data.data;
}

export async function deleteAttendanceLocation(id) {
  const res = await authFetch(`${BASE}/locations/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to delete location"));
  return data;
}

/* ================= HOLIDAYS ================= */

export async function fetchAttendanceHolidays(year) {
  const params = new URLSearchParams();
  if (year != null) params.set("year", String(year));
  const res = await authFetch(`${BASE}/holidays?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load holidays"));
  return data.data ?? { holidays: [], year: new Date().getFullYear(), yearOptions: [] };
}

export async function createAttendanceHoliday(payload) {
  const res = await authFetch(`${BASE}/holidays`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to save holiday"));
  return data.data;
}

export async function updateAttendanceHoliday(id, payload) {
  const res = await authFetch(`${BASE}/holidays/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to update holiday"));
  return data.data;
}

export async function deleteAttendanceHoliday(id) {
  const res = await authFetch(`${BASE}/holidays/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to delete holiday"));
  return data;
}

/* ================= SICK LEAVES ================= */

export async function fetchSickLeaveSummary() {
  const res = await authFetch(`${BASE}/sick-leaves/summary`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load sick leave summary"));
  return data.data ?? { total_users: 0, min_sick_leaves: 0, max_sick_leaves: 0 };
}

export async function updateAllSickLeaves(availableSickLeaves) {
  const res = await authFetch(`${BASE}/sick-leaves/global`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ available_sick_leaves: availableSickLeaves }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to update sick leaves"));
  return data;
}

/* ================= SHIFTS ================= */

export async function fetchAttendanceShifts() {
  const res = await authFetch(`${BASE}/shifts`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load shifts"));
  return Array.isArray(data.data) ? data.data : [];
}

export async function createAttendanceShift(payload) {
  const res = await authFetch(`${BASE}/shifts`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to save shift"));
  return data.data;
}

export async function updateAttendanceShift(id, payload) {
  const res = await authFetch(`${BASE}/shifts/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to update shift"));
  return data.data;
}

export async function deleteAttendanceShift(id) {
  const res = await authFetch(`${BASE}/shifts/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to delete shift"));
  return data;
}
